import { React, useState, useEffect, useRef } from "react";
import styles from "./authHeader.module.css";
import { FaHouse, FaGraduationCap, FaUserGroup } from "react-icons/fa6";
import { FaSearch } from "react-icons/fa";
import { MdOutlineMarkEmailUnread } from "react-icons/md";
import { PiDotsThreeOutlineVertical } from "react-icons/pi";
import { MdAdminPanelSettings } from "react-icons/md";
import { FaRegEnvelope } from "react-icons/fa";
import axios from "axios";
import defaultImage from "../../../appImges/avatartTemplate/default_img.png";
import logo from "./logo.png";
import { Link, useNavigate, NavLink } from "react-router-dom";
import { RiRadioButtonLine } from "react-icons/ri";
import NotificationHandler from "../../notificationsHandler/NotificationsHandler";
import { io } from "socket.io-client";
import { useUser } from "../../../storage/UserContext";

const apiUrl = process.env.REACT_APP_URL;

const AuthHeader = ({ imgLink, username, role, is_online }) => {
  /* This header is used for the logged in users */

  const { userData, hasUnreadMessages, setHasUnreadMessages } = useUser();
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const socket = useRef(null); // Socket instance for listening
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Function to handle resize
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (userData.username && !socket.current) {
      socket.current = io(`${apiUrl}`, {
        transports: ["websocket"],
      });

      // Join the user's room for real-time updates
      socket.current.emit("joinRoom", userData.username);

      // Handle receiving a message and update the unread messages state globally
      socket.current.on("receiveMessage", (message) => {
        if (message.receiver_username === userData.username) {
          setHasUnreadMessages(true); // Mark as unread globally
        }
      });

      return () => {
        if (socket.current) {
          socket.current.disconnect();
          socket.current = null;
        }
      };
    }
  }, [userData.username, setHasUnreadMessages]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await axios.get(`${apiUrl}/notifications`, {
          withCredentials: true,
        });
        if (res.status === 200 && res.data.success) {
          setNotifications(res.data.notifications);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };

    if (isDropdownVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownVisible]);
  // Logout function
  const handleLogout = async () => {
    const res = await axios.post(
      `${apiUrl}/auth/logout`,
      {},
      {
        withCredentials: true,
      }
    );
    if (res.status === 200 && res.data.success) {
      window.location.href = "/";
    }
  };

  const getFullImageUrl = (link) => {
    return link && link !== "default_img_link"
      ? `${apiUrl}/${link}`
      : defaultImage;
  };
  // Dropdown menu function - open and close the dropdown menu when the user clicks on the three dots
  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible);
  };

  // Settings page
  const handleSettingsClick = () => {
    navigate("/settings");
    setDropdownVisible(false); // Close the dropdown
  };

  return (
    <header className={styles.header}>
      <nav className={styles.nav}>
        <NavLink to="/home">
          <figure className={styles.logoFig}>
            <img src={logo} alt="logo" className={styles.logo} />
          </figure>
        </NavLink>

        <div className={styles.leftSide}>
          <div>
            {screenWidth <= 1200 && (
              <NotificationHandler notifications={notifications} />
            )}
          </div>
          <div className={styles.signBlock}>
            <NavLink
              to="/home"
              className={({ isActive }) => (isActive ? styles.activeLink : "")}
            >
              <FaHouse className={styles.sign} />
              <p>Home</p>
            </NavLink>
          </div>

          <div className={styles.signBlock}>
            <NavLink
              to="/learningZones"
              className={({ isActive }) => (isActive ? styles.activeLink : "")}
            >
              <FaGraduationCap className={styles.sign} />
              <p>Learning Zones</p>
            </NavLink>
          </div>

          <div className={styles.signBlock}>
            <NavLink
              to="/pals"
              className={({ isActive }) => (isActive ? styles.activeLink : "")}
            >
              <FaUserGroup className={styles.sign} />
              <p>Pals</p>
            </NavLink>
          </div>

          <div className={styles.signBlock}>
            <NavLink
              to="/search"
              className={({ isActive }) => (isActive ? styles.activeLink : "")}
            >
              <FaSearch className={styles.sign} />
              <p>Search</p>
            </NavLink>
          </div>

          <div className={styles.signBlock}>
            <NavLink
              to="/mailbox"
              className={({ isActive }) => (isActive ? styles.activeLink : "")}
            >
              {hasUnreadMessages ? (
                <MdOutlineMarkEmailUnread
                  className={`${styles.sign} ${styles.unreadMessageIcon}`}
                />
              ) : (
                <FaRegEnvelope className={styles.sign} />
              )}
              <p>Mailbox</p>
            </NavLink>
          </div>
          {role === "Admin" && (
            <div className={styles.signBlock}>
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  isActive ? styles.activeLink : ""
                }
              >
                <MdAdminPanelSettings className={styles.sign} />
                <p>Dashboard</p>
              </NavLink>
            </div>
          )}

          {screenWidth > 1200 && (
            <NotificationHandler notifications={notifications} />
          )}
        </div>

        <div className={styles.rightSide}>
          <div className={styles.dropMenuBlock} ref={dropdownRef}>
            <NavLink to="/profile" className={styles.profileLink}>
              <figure className={styles.userFig}>
                <img
                  className={styles.userPhoto}
                  src={getFullImageUrl(imgLink)}
                  alt={username}
                  onError={(e) => {
                    e.target.src = defaultImage;
                    e.target.onerror = null;
                  }}
                />
                <figcaption>
                  <span>{username}</span>{" "}
                  <span className={styles.online}>
                    {is_online && <RiRadioButtonLine />}
                  </span>
                </figcaption>
              </figure>
            </NavLink>
            <div onClick={toggleDropdown}>
              <PiDotsThreeOutlineVertical className={styles.sign} />
              {isDropdownVisible && (
                <div className={styles.dropdownMenu}>
                  <ul>
                    <li>
                      <NavLink to="/profile" className={styles.customLink}>
                        Profile
                      </NavLink>
                    </li>
                    <li onClick={handleSettingsClick}>Settings</li>
                    <li onClick={handleLogout}>Logout</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default AuthHeader;
